import { call, put, select } from 'redux-saga/effects';

import { updateUserAndPatient } from 'connect/actions';
import { extractErrorMessage, sendToLogger } from 'connect/saga/helpers';

import { apiGetUser } from 'endpoints/patient-endpoints';
import { CheckoutStateHandler } from 'pages/checkout/connect/state-handlers';
import { POST, request } from 'utils/request';

import { UpdatePatientDetails } from '../../patient/update-details';
import { SagaHandler } from '../../SagaHandler';

const apiVerifyOtp = ({ email, code }) => request(
  POST,
  `${CONFIG.API_URL}/auth/patient-otp/verify`,
  {
    email,
    code,
    includePatient: true,
  },
);

/*
  Checks if an OTP has been sent to the user
  If not, logs an error and requests a new OTP to be sent
*/
class _VerifyOtp extends SagaHandler {
  constructor() {
    super({
      actionName: 'data/VERIFY_OTP',
      stateKey: 'verifyOtp',
    });
  }

  createAction({ code }) {
    return {
      type: this._actionName,
      code,
    };
  }

  * saga({ code }) {
    const errorHandler = this.error;
    const loadingHandler = this.loading;
    const valueHandler = this.value;
    const successHandler = this.success;

    try {
      const checkoutState = yield select(CheckoutStateHandler.selector);
      const { account } = checkoutState || {};
      const { email, phoneToSave } = account || {};

      if (!email) {
        throw new Error('No email in Redux state, cannot verify OTP');
      }

      yield put(loadingHandler.update(true));
      yield put(errorHandler.update(null));

      // This will throw if it fails
      yield call(apiVerifyOtp, { email, code });

      const populatedUser = yield call(apiGetUser);

      let [patient] = populatedUser.editable?.patients || [];

      // phoneToSave is set if the user doesn't have a phone number saved yet
      if (phoneToSave && patient?.id) {
        const updatedPatient = yield call(UpdatePatientDetails.saga, {
          // We don't want to updateUserAndPatient yet to prevent side effects firing too early,
          // so we need to force UpdatePatientDetails not to check the logged in status
          checkLoginStatus: false,
          patientId: patient.id,
          patientDetails: {
            mobilePhone: phoneToSave,
          },
        });

        if (!updatedPatient) {
          throw new Error('Failed to update phone number');
        }

        patient = updatedPatient;

        yield put(CheckoutStateHandler.update({
          ...checkoutState,
          account: {
            ...account,
            phoneToSave: null,
          },
        }));
      }

      // Update populated user with the updated patient
      populatedUser.editable.patients = [patient];

      yield put(updateUserAndPatient(populatedUser));
      yield put(successHandler.update(true));
    } catch (error) {
      const errorFromApi = error.response?.data;
      const errorMessage = extractErrorMessage(error);

      yield put(errorHandler.update(errorFromApi || errorMessage));
      yield put(valueHandler.update(null));

      sendToLogger(error, 'VerifyOtp');
    } finally {
      yield put(loadingHandler.update(false));
    }
  }
}

export const VerifyOtp = new _VerifyOtp();
